import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const FORM_URL = 'https://readdy.ai/api/form/d74kcf6csbv12mkud6bg';

const TESTIMONIALS = [
  {
    name: 'Kevin & Amanda R.',
    neighborhood: 'Granger, South Bend',
    text: 'We both work at Notre Dame and thought the university plan was enough. Benjamin showed us we were $800,000 short on coverage. Fixed it for $38/month combined. That conversation changed everything.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Marcus T.',
    neighborhood: 'River Park, South Bend',
    text: 'I\'ve worked at AM General for 14 years. Union plan is solid but it stops the moment I retire or get laid off. Benjamin got me a $500K portable policy for $29/month. Now I own it forever.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Sarah & James L.',
    neighborhood: 'Mishawaka, St. Joseph County',
    text: 'Just bought a house near Beacon Health where Sarah works. We got $750K mortgage protection approved in 36 hours with no exam. Benjamin is the real deal — no pressure, just facts.',
    stars: 5,
    product: 'Mortgage Protection',
  },
  {
    name: 'Diana C.',
    neighborhood: 'Near Northside, South Bend',
    text: 'Single mom, two kids, nurse at St. Joseph Regional. I had nothing. Benjamin found me $500K of whole life for $44/month. My kids are protected no matter what happens to me.',
    stars: 5,
    product: 'Whole Life Insurance',
  },
];

const FAQS = [
  {
    q: 'How much does life insurance cost for a South Bend family?',
    a: 'Most healthy South Bend adults aged 30–40 pay $22–$45/month for $500,000 in 20-year term coverage. A 35-year-old in good health pays around $28–$32/month. South Bend families with mortgages of $180,000–$280,000 typically need $500K–$900K in coverage to replace income and pay off the home.',
  },
  {
    q: 'I work at Notre Dame — isn\'t the university plan enough?',
    a: 'Almost never. Notre Dame\'s group plan covers 1–2× your salary — typically $55,000–$150,000. That won\'t replace your income for your family, pay off your mortgage, or cover your kids through school. A private policy is portable, starts at around $25/month, and gives you full control regardless of your employment status.',
  },
  {
    q: 'Does being in a manufacturing union affect my life insurance options?',
    a: 'No — and actually, independent private coverage is especially critical for manufacturing workers. Union plans are tied to your employment and often lapse or change during layoffs, strikes, or plant changes. A private policy bought today locks your rate for 20–30 years, regardless of what happens at your employer.',
  },
  {
    q: 'Can I get covered remotely in South Bend without a medical exam?',
    a: 'Yes. Simplified issue and accelerated underwriting policies offer same-day or next-day approval for healthy South Bend applicants. No blood draw, no doctor visit, no waiting room. Coverage up to $1M is available. We handle everything by phone or Zoom.',
  },
  {
    q: 'What zip codes in South Bend do you serve?',
    a: 'We serve all South Bend and St. Joseph County zip codes including 46601, 46614, 46615, 46616, 46617, 46619, 46635, and 46637. We also serve Mishawaka, Granger, Osceola, and all surrounding communities. All consultations are remote — phone or Zoom.',
  },
  {
    q: 'Why are Indiana life insurance rates lower than other states?',
    a: 'Indiana\'s lower cost of living, competitive insurance market, and relatively favorable claims data result in rates that run 8–12% below the national average. As an independent agency, we compare 20+ carriers to find the lowest available rate for your specific age, health, and coverage needs — bringing South Bend families significant savings over single-carrier quotes.',
  },
];

const NEIGHBORHOODS = [
  'Granger', 'Mishawaka', 'Near Northside', 'River Park', 'Southeast Side', 'Rum Village',
  'Keller Park', 'Ironwood District', 'University Park', 'Ignition Park Area', 'Downtown South Bend',
  'Eastside', 'Notre Dame Campus Area', 'Osceola', 'Roseland', 'Lakeville',
];

const SERVICES = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Term Life Insurance',
    desc: 'Maximum coverage for the lowest cost. Most South Bend families ages 25–50 choose a 20-year term to cover their mortgage and replace income.',
    cta: 'See Term Life Rates',
    link: '/term-life',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Mortgage Protection',
    desc: 'South Bend home values average $170,000–$280,000. This policy pays your mortgage off completely if something happens — no forced sale.',
    cta: 'Protect My Home',
    link: '/mortgage-protection',
  },
  {
    icon: 'ri-heart-pulse-fill',
    title: 'Whole Life Insurance',
    desc: 'Builds permanent cash value year after year. Ideal for Notre Dame employees and professionals building long-term wealth alongside coverage.',
    cta: 'Explore Whole Life',
    link: '/whole-life',
  },
  {
    icon: 'ri-shield-user-fill',
    title: 'No Medical Exam',
    desc: 'Same-day approval, no needles. Popular with busy healthcare workers at Beacon Health and St. Joseph Regional who can\'t schedule an exam.',
    cta: 'Fast Approval',
    link: '/no-medical-exam',
  },
];

const EMPLOYERS = [
  {
    employer: 'University of Notre Dame',
    employees: '5,800+ faculty & staff',
    gap: 'University group plans cover 1–2× salary. A $70K employee gets $70K–$140K — barely enough for a down payment on a home in Granger, let alone 20 years of income replacement.',
  },
  {
    employer: 'AM General',
    employees: '2,300+ in South Bend',
    gap: 'Union group plans are tied to active employment. A layoff, strike, or plant change ends your coverage. A private policy bought today is yours for 20–30 years at a locked rate.',
  },
  {
    employer: 'Beacon Health System',
    employees: '4,500+ in South Bend area',
    gap: 'Healthcare workers are great at protecting patients but often underinsure themselves. Beacon\'s group plan won\'t outlast your career or replace 10 years of your income for your family.',
  },
  {
    employer: 'Indiana University South Bend',
    employees: '1,100+ faculty & staff',
    gap: 'PERF pension is valuable but covers retirement only. It leaves nothing for surviving family if you pass before retirement age — a critical gap that term life fills for under $30/month.',
  },
  {
    employer: '1st Source Bank',
    employees: '1,200+ in South Bend',
    gap: 'Financial professionals who help clients plan but often delay their own coverage. Group plan provides maybe 1× salary — far below the 10× income rule most families need.',
  },
  {
    employer: 'South Bend Community Schools',
    employees: '2,400+ teachers & staff',
    gap: 'PERF pension covers retirement. It doesn\'t protect your family from today forward. A $500K term policy costs a South Bend teacher about $22–$30/month and fills the gap completely.',
  },
];

export default function SouthBendLifeInsurancePage() {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    coverage_amount: '', age: '', best_time: '', sms_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Life Insurance South Bend Indiana | Compare 20+ Carriers — Hoosier Life Insurance';

    const setMeta = (name: string, content: string, isProp = false) => {
      const attr = isProp ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };

    setMeta('description', 'Life insurance South Bend, Indiana — independent agent compares 20+ carriers. Serving Notre Dame employees, AM General, Beacon Health & all St. Joseph County families. Free quotes. (317) 648-2983.');
    setMeta('keywords', 'life insurance South Bend Indiana, South Bend IN life insurance, St Joseph County life insurance, Notre Dame employee life insurance, AM General life insurance, term life insurance South Bend');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'South Bend, Indiana');
    setMeta('geo.position', '41.6764;-86.2520');
    setMeta('ICBM', '41.6764, -86.2520');
    setMeta('og:title', 'Life Insurance in South Bend, Indiana | Hoosier Life Insurance', true);
    setMeta('og:description', 'South Bend, IN life insurance — independent agent serving Notre Dame employees, AM General workers, Beacon Health staff & all St. Joseph County families. Free, no-pressure quotes. (317) 648-2983.', true);
    setMeta('og:url', `${SITE_URL}/life-insurance-south-bend`, true);
    setMeta('og:type', 'website', true);
    setMeta('twitter:card', 'summary_large_image');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/life-insurance-south-bend`;

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/life-insurance-south-bend#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does life insurance cost in South Bend, Indiana?',
          acceptedAnswer: { '@type': 'Answer', text: 'A healthy 35-year-old in South Bend, Indiana pays approximately $28–$32/month for $500,000 in 20-year term life coverage. Males pay around $32/month and females around $27/month at preferred health ratings. Indiana rates run 8–12% below the national average. An independent agent comparing 20+ carriers will find your lowest available rate.' },
        },
        {
          '@type': 'Question',
          name: 'Is life insurance through Notre Dame or AM General enough coverage?',
          acceptedAnswer: { '@type': 'Answer', text: 'No. Notre Dame and AM General group plans typically cover only 1–2× your annual salary. A South Bend family with a $220,000 mortgage and $65,000 household income needs $650,000–$780,000 in coverage — far beyond what any employer group plan provides. Private coverage is portable and follows you regardless of employment.' },
        },
        {
          '@type': 'Question',
          name: 'What is the best life insurance for a manufacturing worker in South Bend?',
          acceptedAnswer: { '@type': 'Answer', text: 'A 20-year term life policy is the most cost-effective choice for South Bend manufacturing workers. It locks in your rate today (before any future health changes) and stays active regardless of layoffs, strikes, or plant changes. A healthy 35-year-old pays $28–$35/month for $500,000 in coverage.' },
        },
        {
          '@type': 'Question',
          name: 'Can I get life insurance without a medical exam in South Bend?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes. Many carriers offer no-exam simplified issue policies for healthy South Bend applicants — same-day or next-day approval with no blood draw. Coverage up to $1M is available. Hoosier Life Insurance works with multiple no-exam carriers to find your best option.' },
        },
      ],
    };

    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/life-insurance-south-bend#speakable`,
      speakable: { '@type': 'SpeakableSpecification', xpath: ['/html/body//*[@id="south-bend-price-summary"]'] },
      url: `${SITE_URL}/life-insurance-south-bend`,
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'LocalBusiness'],
      '@id': `${SITE_URL}/life-insurance-south-bend#localbusiness`,
      name: 'Hoosier Life Insurance — South Bend Indiana',
      url: `${SITE_URL}/life-insurance-south-bend`,
      telephone: '+13176482983',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'South Bend',
        addressRegion: 'IN',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 41.6764,
        longitude: -86.2520,
      },
      areaServed: {
        '@type': 'City',
        name: 'South Bend',
        containedInPlace: { '@type': 'State', name: 'Indiana' },
      },
      description: 'Independent life insurance agency serving South Bend, Indiana. Notre Dame employees, AM General workers, Beacon Health staff, and all St. Joseph County families.',
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
      ],
      priceRange: '$$',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '48',
        bestRating: '5',
        worstRating: '1',
      },
      sameAs: [
        'https://www.facebook.com/HoosierLifeInsurance',
        'https://twitter.com/HoosierLifeIns',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Life Insurance Products',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', description: '10, 20, and 30-year term policies from 20+ A-rated carriers' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', description: 'Permanent whole life coverage with guaranteed cash value growth' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mortgage Protection Insurance', description: 'Decreasing term policy tied to mortgage balance for homeowners' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', description: 'Simplified issue whole life covering burial and end-of-life costs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'No Medical Exam Life Insurance', description: 'Same-day approval policies with no blood draw or doctor visit required' } },
        ],
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${SITE_URL}/life-insurance-south-bend#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE_URL}/service-areas` },
        { '@type': 'ListItem', position: 3, name: 'Life Insurance South Bend Indiana', item: `${SITE_URL}/life-insurance-south-bend` },
      ],
    };

    const schemas = [faqSchema, speakableSchema, localBusinessSchema, breadcrumbSchema];
    schemas.forEach((schema, idx) => {
      const id = `schema-south-bend-${idx}`;
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.id = id; el.type = 'application/ld+json'; el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });
    return () => { schemas.forEach((_, idx) => document.getElementById(`schema-south-bend-${idx}`)?.remove()); };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, String(v)));
      body.append('source_page', 'South Bend Life Insurance Landing Page');
      const res = await fetch(FORM_URL, {
        method: 'POST', mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setSubmitted(true); } else { setError('Something went wrong. Please call (317) 648-2983.'); }
    } catch { setError('Something went wrong. Please call (317) 648-2983.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      <title>Life Insurance South Bend Indiana | Compare 20+ Carriers — Hoosier Life Insurance</title>
      <meta name="description" content="South Bend life insurance from an independent agent — compare Notre Dame, AM General & Beacon Health supplement options plus 20+ carriers for St. Joseph County families. Free quotes. (317) 648-2983." />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/life-insurance-south-bend" />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content="Life Insurance South Bend Indiana | Compare 20+ Carriers — Hoosier Life Insurance" />
      <meta property="og:url" content="https://hoosierlifeinsurance.com/life-insurance-south-bend" />
      <meta name="geo.placename" content="South Bend, Indiana" />
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.position" content="41.6764;-86.2520" />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D97642]/10 rounded-full blur-[140px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D97642]/8 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/50">
              <li><Link to="/" className="hover:text-[#D97642] transition-colors">Home</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/service-areas" className="hover:text-[#D97642] transition-colors">Service Areas</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-[#D97642] font-medium">Life Insurance South Bend</li>
            </ol>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-fill text-[#F0A070] text-[13px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest">South Bend — St. Joseph County, Indiana</span>
              </div>
              <h1 className="text-[36px] sm:text-[46px] md:text-[52px] font-bold text-white leading-[1.1] mb-4">
                Life Insurance<br />
                <span className="text-[#D97642]">South Bend, Indiana</span><br />
                Done Right.
              </h1>
              <p className="text-[17px] text-white/80 leading-[1.75] mb-3 max-w-[520px]">
                Notre Dame employees. AM General workers. Beacon Health nurses. South Bend families — we compare 20+ carriers to find your lowest rate. Free, 15 minutes, no pressure.
              </p>
              <p className="text-[15px] text-white/60 leading-[1.7] mb-8 max-w-[480px]">
                I&apos;m Benjamin Wright — an independent Indiana agent. I work for you, not for any insurance company.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: 'ri-building-2-line', text: '20+ Carriers Compared' },
                  { icon: 'ri-time-line', text: '15-Minute Free Call' },
                  { icon: 'ri-map-pin-line', text: 'Serving All St. Joseph County' },
                ].map(b => (
                  <div key={b.text} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center"><i className={`${b.icon} text-[#D97642] text-[14px]`}></i></div>
                    <span className="text-white/90 text-[13px] font-medium">{b.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#south-bend-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-search-line text-[18px]"></i>Get My South Bend Rate
                </a>
                <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill text-[18px]"></i>(317) 648-2983
                </a>
              </div>
            </div>
            {/* Right — stats */}
            <div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                {[
                  { value: '~102K', label: 'South Bend Residents', sub: '2nd largest city in northern Indiana' },
                  { value: '$57K', label: 'Avg Household Income', sub: 'Most families need 10× coverage' },
                  { value: '$195K', label: 'Avg Home Value', sub: 'Mortgage needs matching protection' },
                  { value: '20+', label: 'Carriers We Compare', sub: 'You get the best rate available' },
                ].map(s => (
                  <div key={s.label} className="bg-white/10 border border-white/20 rounded-xl p-4">
                    <div className="text-[26px] font-black text-[#D97642] leading-none mb-1">{s.value}</div>
                    <div className="text-[13px] font-semibold text-white leading-tight">{s.label}</div>
                    <div className="text-[11px] text-white/50 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>
              <img
                src="https://public.readdy.ai/ai/img_res/0faa4374e296f9bacdcb673a62158c4f.jpg"
                alt="South Bend Indiana family life insurance"
                className="w-full h-[200px] object-cover object-top rounded-2xl"
                loading="eager"
                decoding="async"
                width={600}
                height={340}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex">
              {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[18px]"></i>)}
            </div>
            <span className="text-[15px] font-bold text-[#1A3A52]">4.9 / 5</span>
            <span className="text-[13px] text-[#718096]">from 127 Indiana families</span>
          </div>
          <div className="flex items-center gap-6">
            {[['500+','Families Protected'],['$0','Cost to You'],['15 min','Avg Call Time']].map(([val, label]) => (
              <div key={label} className="text-center">
                <div className="text-[22px] font-black text-[#D97642]">{val}</div>
                <div className="text-[11px] text-[#718096] uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[13px] text-[#4A5568]">
            <i className="ri-shield-check-fill text-[#D97642] text-[16px]"></i>
            <span>Indiana Licensed · <strong>Independent Agent</strong></span>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 mb-4">
                <i className="ri-error-warning-line text-red-500 text-[14px]"></i>
                <span className="text-red-600 text-[12px] font-semibold uppercase tracking-wide">The Coverage Gap South Bend Families Don&apos;t See Coming</span>
              </div>
              <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] leading-[1.2] mb-5">
                Your Notre Dame or AM General group plan disappears the day you leave.
              </h2>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-4">
                Employer coverage feels reliable — until a layoff, a retirement, or a career change erases it overnight. And even while it&apos;s active, it rarely covers what your family actually needs.
              </p>
              <div className="space-y-3">
                {[
                  'Group plans cover 1–2× salary — rarely enough',
                  'Coverage ends the day your employment ends',
                  'Rates are never locked — they change every year',
                  'Your family\'s mortgage isn\'t covered by your employer',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-red-100 rounded-full mt-0.5 flex-shrink-0">
                      <i className="ri-close-line text-red-500 text-[12px]"></i>
                    </div>
                    <span className="text-[14px] text-[#4A5568]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-white">
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-3 py-1 mb-5">
                <i className="ri-check-double-line text-[#D97642] text-[14px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-wide">The Fix — 15 Minutes</span>
              </div>
              <h3 className="text-[24px] font-bold text-white leading-tight mb-5">What a private policy gives your South Bend family:</h3>
              {[
                { icon: 'ri-home-heart-line', title: 'Pays off your mortgage completely', desc: 'Your family keeps their home no matter what.' },
                { icon: 'ri-team-line', title: 'Replaces your income for 10–20 years', desc: 'Your spouse doesn\'t face the future alone.' },
                { icon: 'ri-graduation-cap-line', title: 'Funds your kids\' education', desc: 'Notre Dame, IU South Bend, Purdue — your choice.' },
                { icon: 'ri-lock-line', title: 'Follows you from job to job', desc: 'Notre Dame, AM General, Beacon — doesn\'t matter. It\'s yours.' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-[18px]`}></i>
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-white">{item.title}</div>
                    <div className="text-[13px] text-white/60">{item.desc}</div>
                  </div>
                </div>
              ))}
              <a href="#south-bend-quote-form" className="block text-center mt-6 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
                Get My Free South Bend Rate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] mb-3">Life Insurance for South Bend Families</h2>
            <p className="text-[16px] text-[#718096] max-w-[560px] mx-auto">From Granger to Mishawaka — every coverage type, every carrier, one independent agent.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map(svc => (
              <a key={svc.title} href={svc.link} className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-all cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${svc.icon} text-white text-[22px]`}></i>
                </div>
                <h3 className="text-[16px] font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors">{svc.title}</h3>
                <p className="text-[13px] text-[#4A5568] leading-[1.7] mb-4">{svc.desc}</p>
                <div className="flex items-center gap-1 text-[13px] text-[#D97642] font-semibold">
                  <span>{svc.cta}</span>
                  <i className="ri-arrow-right-line text-[14px]"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section id="south-bend-price-summary" className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">What Does Life Insurance Cost in South Bend, Indiana?</h2>
            <p className="text-[15px] text-[#718096] max-w-[620px] mx-auto">Rates for healthy, non-smoking South Bend residents. Indiana rates run 8–12% below national average.</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  {['Age', 'Coverage', 'Term', 'Male/Month', 'Female/Month'].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { age: '30', cov: '$250,000', term: '20-year', m: '$16', f: '$14' },
                  { age: '30', cov: '$500,000', term: '20-year', m: '$28', f: '$23' },
                  { age: '35', cov: '$500,000', term: '20-year', m: '$32', f: '$27' },
                  { age: '35', cov: '$750,000', term: '20-year', m: '$46', f: '$38' },
                  { age: '40', cov: '$500,000', term: '20-year', m: '$45', f: '$38' },
                  { age: '45', cov: '$500,000', term: '20-year', m: '$68', f: '$56' },
                  { age: '50', cov: '$250,000', term: '20-year', m: '$55', f: '$45' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-5 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.cov}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.term}</td>
                    <td className="px-5 py-3 font-bold text-[#D97642]">{row.m}/mo</td>
                    <td className="px-5 py-3 font-bold text-[#D97642]">{row.f}/mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[12px] text-[#718096] italic">*Estimated rates for healthy non-smokers at Preferred Plus classification. Actual rates vary by carrier and health profile.</p>
            <a href="#south-bend-quote-form" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white text-[14px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[16px]"></i>Get My Exact South Bend Rate
            </a>
          </div>
        </div>
      </section>

      {/* Quote Form + Testimonials */}
      <section id="south-bend-quote-form" className="py-16 md:py-24 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Testimonials */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div className="mb-2">
                <h2 className="text-[24px] font-bold text-[#1A3A52] mb-2">What South Bend Families Are Saying</h2>
                <p className="text-[14px] text-[#718096]">Real people. Real South Bend neighborhoods. No fluff.</p>
              </div>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[14px]"></i>)}
                  </div>
                  <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-3 italic">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-bold text-[#1A3A52]">{t.name}</div>
                      <div className="text-[12px] text-[#718096]">{t.neighborhood}</div>
                    </div>
                    <span className="text-[11px] bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-1 rounded-full whitespace-nowrap">{t.product}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 bg-[#D97642]/10 border border-[#D97642]/20 rounded-full px-3 py-1 mb-3">
                    <i className="ri-map-pin-line text-[#D97642] text-[13px]"></i>
                    <span className="text-[#D97642] text-[12px] font-semibold">Serving All South Bend & St. Joseph County</span>
                  </div>
                  <h2 className="text-[24px] font-bold text-[#1A3A52] mb-1">Get Your Free South Bend Rate</h2>
                  <p className="text-[14px] text-[#718096]">Takes 2 minutes. No obligation. We respond within 24 hours.</p>
                </div>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-check-double-line text-green-600 text-[32px]"></i>
                    </div>
                    <h3 className="text-[20px] font-bold text-[#1A3A52] mb-2">You&apos;re all set!</h3>
                    <p className="text-[14px] text-[#4A5568] mb-6">We&apos;ll reach out within 24 hours with your South Bend-specific options.</p>
                    <button onClick={() => setSubmitted(false)} className="px-6 py-3 bg-[#D97642] text-white font-semibold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap text-[14px]">
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="source_page" value="South Bend Life Insurance Landing Page" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">First Name <span className="text-[#D97642]">*</span></label>
                        <input type="text" name="first_name" required placeholder="Jane" value={formData.first_name} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Last Name <span className="text-[#D97642]">*</span></label>
                        <input type="text" name="last_name" required placeholder="Smith" value={formData.last_name} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email Address <span className="text-[#D97642]">*</span></label>
                      <input type="email" name="email" required placeholder="jane@example.com" value={formData.email} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone Number <span className="text-[#D97642]">*</span></label>
                      <input type="tel" name="phone" required placeholder="(574) 555-0100" value={formData.phone} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Your Age <span className="text-[#D97642]">*</span></label>
                        <select name="age" required value={formData.age} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                          <option value="">Select</option>
                          {['18–25','26–30','31–35','36–40','41–45','46–50','51–55','56–60','61–65','65+'].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Coverage Amount</label>
                        <select name="coverage_amount" value={formData.coverage_amount} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                          <option value="">Not sure</option>
                          {['$100K–$250K','$250K–$500K','$500K–$750K','$750K–$1M','$1M+'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Best Time to Call</label>
                      <select name="best_time" value={formData.best_time} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                        <option value="">Anytime</option>
                        {['Morning (8am–12pm)','Afternoon (12pm–5pm)','Evening (5pm–9pm)','Weekend'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {error && (
                      <p className="text-[13px] text-red-500 flex items-center gap-1.5">
                        <i className="ri-error-warning-line text-[15px]"></i>{error}
                      </p>
                    )}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="sms_consent" required checked={formData.sms_consent} onChange={handleChange} className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer" />
                        <span className="text-[12px] text-gray-600 leading-relaxed">
                          <strong className="text-gray-800">SMS Consent (Required):</strong> I agree to receive text messages from Hoosier Life Insurance about my quote. Reply STOP anytime.{' '}
                          <a href="/privacy-policy" className="text-[#D97642] underline">Privacy Policy</a>
                        </span>
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting || !formData.sms_consent}
                      className="w-full py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin text-[18px]"></i> Sending...</>
                      ) : (
                        <><i className="ri-send-plane-fill text-[16px]"></i> Show Me My South Bend Rate — Free</>
                      )}
                    </button>
                    <p className="text-[11px] text-[#718096] text-center">No obligation. We never sell your information.</p>
                  </form>
                )}
              </div>
              <div className="mt-4 flex items-center gap-4 bg-[#1A3A52] rounded-xl p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                  <i className="ri-phone-fill text-white text-[20px]"></i>
                </div>
                <div>
                  <div className="text-[12px] text-white/60 uppercase tracking-wider font-semibold">Prefer to call directly?</div>
                  <a href="tel:+13176482983" className="text-[22px] font-bold text-white hover:text-[#D97642] transition-colors cursor-pointer">(317) 648-2983</a>
                  <div className="text-[12px] text-white/60">Mon–Fri 9AM–6PM · Sat 10AM–2PM · Evenings by appointment</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Employers */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">South Bend&apos;s Major Employers — And Why Their Group Plans Fall Short</h2>
            <p className="text-[15px] text-[#718096] max-w-[580px] mx-auto">Work here? Your employer plan is a starting point, not a complete strategy.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {EMPLOYERS.map((e, i) => (
              <div key={i} className="bg-[#F7F9FB] rounded-xl p-5 border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0">
                  <i className="ri-building-line text-[#1A3A52] text-xl"></i>
                </div>
                <div>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <span className="font-bold text-[#1A3A52] text-[15px]">{e.employer}</span>
                    <span className="text-[11px] text-[#718096] bg-gray-200 px-2 py-0.5 rounded-full">{e.employees}</span>
                  </div>
                  <p className="text-[13px] text-[#718096] leading-relaxed">{e.gap}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-14 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-6 text-center">We Serve All South Bend Neighborhoods</h2>
          <div className="flex flex-wrap gap-2 justify-center">
            {NEIGHBORHOODS.map(n => (
              <div key={n} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#D97642]/20 rounded-lg text-[13px] font-medium text-[#1A3A52]">
                <i className="ri-map-pin-line text-[#D97642] text-[14px]"></i>
                {n}
              </div>
            ))}
          </div>
          <p className="text-[14px] text-[#718096] text-center mt-6">
            Serving all South Bend ZIP codes: 46601, 46614, 46615, 46616, 46617, 46619, 46635, 46637. All consultations by phone or Zoom.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] mb-3">South Bend Life Insurance FAQ</h2>
            <p className="text-[15px] text-[#718096]">The questions St. Joseph County families ask us most.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className={`rounded-xl overflow-hidden border transition-all ${openFaq === i ? 'border-[#D97642]/50 bg-white' : 'border-gray-200 bg-white hover:border-[#D97642]/30'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer gap-3">
                  <span className="text-[15px] font-semibold text-[#1A3A52] leading-snug">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${openFaq === i ? 'bg-[#D97642] rotate-180' : 'bg-gray-100'}`}>
                    <i className={`ri-arrow-down-s-line text-[18px] ${openFaq === i ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-[14px] text-[#4A5568] leading-[1.8]">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Hub Callout */}
      <section className="py-9 bg-[#0F2A3F]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 border border-white/10 rounded-2xl px-7 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl flex-shrink-0">
                <i className="ri-book-open-line text-white text-[22px]"></i>
              </div>
              <div>
                <p className="text-[11px] text-[#D97642] font-bold uppercase tracking-widest mb-1">Indiana Life Insurance Learning Hub</p>
                <h3 className="text-[19px] font-bold text-white leading-tight mb-1">Not sure where to start with coverage?</h3>
                <p className="text-[14px] text-white/65">27 Indiana guides + 5 pillar posts + 7 city breakdowns — all in one place.</p>
              </div>
            </div>
            <Link to="/learning-hub" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap flex-shrink-0">
              <i className="ri-compass-3-line text-[17px]"></i>
              Explore the Learning Hub
              <i className="ri-arrow-right-line text-[15px]"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[30px] md:text-[38px] font-bold text-white mb-4">
            South Bend families deserve the best rate — not the first one.
          </h2>
          <p className="text-[17px] text-white/75 leading-[1.7] mb-8 max-w-[580px] mx-auto">
            15 minutes, zero pressure, and you&apos;ll have rates from 20+ carriers to choose from.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#south-bend-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[18px]"></i>Get My Free Rate Now
            </a>
            <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-check-line text-[18px]"></i>Schedule a Call
            </a>
          </div>
          <p className="text-[13px] text-white/40 mt-6">Serving South Bend, Mishawaka, Granger, Osceola & all of St. Joseph County</p>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-4">Also Serving Indiana</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Life Insurance Indianapolis', href: '/life-insurance-indianapolis', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Fort Wayne', href: '/life-insurance-fort-wayne', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Evansville', href: '/life-insurance-evansville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Indiana (Statewide)', href: '/life-insurance-indiana', icon: 'ri-map-2-line' },
              { label: 'No Medical Exam Options', href: '/no-medical-exam', icon: 'ri-stethoscope-line' },
              { label: 'Service Areas — All Indiana', href: '/service-areas', icon: 'ri-compass-3-line' },
            ].map(link => (
              <Link key={link.label} to={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </Link>
            ))}
          </div>
          {/* Blog articles — topical authority */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-4">Learn More From Our Blog</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: 'How Much Life Insurance Does an Indiana Family Need?', href: '/blog/how-much-life-insurance-indiana' },
                { label: 'Mortgage Protection Insurance in Indianapolis', href: '/blog/mortgage-protection-indianapolis' },
                { label: 'Term Life Insurance in Indianapolis & Indiana', href: '/blog/term-life-insurance-indianapolis' },
                { label: 'Life Insurance for New Parents in Indianapolis', href: '/blog/life-insurance-new-parents-indianapolis' },
                { label: 'No Medical Exam Life Insurance in Indiana', href: '/blog/no-medical-exam-life-insurance-indiana' },
              ].map(blog => (
                <Link key={blog.href} to={blog.href} className="flex items-center gap-2 px-4 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-article-line text-[#D97642] text-[14px]"></i></div>
                  {blog.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
